import {
  AutoCompleteField,
  Check,
  CheckGroup,
  form,
  Form as AppForm,
  FormInput,
  FormRow,
  NumberInput,
  Select,
  TextArea
} from "MODULES/shared/components/Forms";
import React, { useEffect, useState } from "react";
import { Field, FormSection } from "redux-form";
import { COMPANY_ID, CONTACT_ID, NOTES } from "APP_ROOT/constants";
import { arrayToProperCasedOptions, toOptions } from "LIB/forms";
import { properCase } from "LIB/formatters/text";
import { Col } from "react-bootstrap";
import {
  AVG_OVERHAUL,
  AVG_REPAIR,
  AVG_TEST,
  AWAITING_REPLY,
  BER_RATE,
  EVALUATION_FEE,
  NO_CAPES,
  OVERHAUL_TAT,
  QUOTE_TYPE,
  QUOTE_TYPES,
  REPAIR_QUOTE_LINE_ITEM,
  REPAIR_TAT,
  TEST_TAT
} from "MODULES/RepairQuotes/constants";
import CurrencyField from "MODULES/shared/components/Forms/CurrencyField";
import { CompanyStatsTooltip } from "MODULES/shared/components";
import {
  ACTIVE,
  APPROVAL_STATUSES,
  CONTACT,
  CREDIT_HOLD
} from "MODULES/companies/constants";
import { withFieldAccessors } from "MODULES/shared/decorators/withFieldAccessors";
import api from "APP_ROOT/api";
import { contactSelectList } from "MODULES/companies/helpers";
import { toastr } from "react-redux-toastr";
import { clearForNoCapes } from "MODULES/RepairQuotes/helpers";
import { REPAIR_QUOTE_WARRANTY_PERIODS } from "MODULES/parts/constants";
import validate from "./validate";

const FORM_NAME = "repairQuoteForm";

function Form(props) {
  const { getFieldValue, repairQuote, setFieldValue, submitting } = props;

  const company = repairQuote && repairQuote.company;
  const contact = getFieldValue(CONTACT_ID);
  const [contacts, setContacts] = useState([]);
  const companyId = getFieldValue(COMPANY_ID);
  const quoteType = getFieldValue(
    [REPAIR_QUOTE_LINE_ITEM, QUOTE_TYPE].join(".")
  );
  const isNoCape = quoteType === NO_CAPES;
  const isAwaitingReply = quoteType === AWAITING_REPLY;

  useEffect(
    () => {
      if (companyId) {
        api.companies.contacts
          .search(companyId, {
            active: true,
            page: 1,
            limit: 1000,
            sort: "name",
            reverse: false
          })
          .then(({ data }) => setContacts(data.results));
      }
    },
    [companyId]
  );

  return (
    <AppForm {...props} submitting={submitting} modal={props.modal}>
      <AppForm.Body>
        <FormRow>
          <Col md={6}>
            <Field
              name={[REPAIR_QUOTE_LINE_ITEM, QUOTE_TYPE].join(".")}
              component={Select}
              options={arrayToProperCasedOptions(QUOTE_TYPES)}
              label={properCase(QUOTE_TYPE)}
              onChange={ev => clearForNoCapes(ev, setFieldValue)}
              required
            />
          </Col>
          {quoteType === AWAITING_REPLY && (
            <Col md={6}>
              <CheckGroup justified>
                <Field name={"sendRfq"} component={Check} label="Send RFQ" />
              </CheckGroup>
            </Col>
          )}
        </FormRow>
        <FormRow>
          <Col md={6}>
            <Field
              name={COMPANY_ID}
              label="Repair Station"
              labelChildren={
                company && (
                  <React.Fragment>
                    &nbsp;&nbsp;
                    <CompanyStatsTooltip company={company} />
                  </React.Fragment>
                )
              }
              placeholder="Search by typing a company's name or code"
              component={AutoCompleteField}
              search={filter =>
                api.companies
                  .search({
                    filter,
                    status: [ACTIVE, CREDIT_HOLD].join(","),
                    page: 1,
                    limit: 20
                  })
                  .then(resp => resp.data.results)}
              formatItem={result =>
                `${result.name} (${APPROVAL_STATUSES[result.vendor_status]
                  .label})`}
              selectedItem={company}
              required
            />
          </Col>
          <Col md={3}>
            <Field
              name={CONTACT_ID}
              component={Select}
              options={contactSelectList(contacts)}
              label={properCase(CONTACT)}
              required
            />
          </Col>
        </FormRow>
        {contact === "Add" && (
          <FormRow>
            <Col md={6}>
              <div className="form-group">
                <Field
                  name="contact.name"
                  component={FormInput}
                  label="Contact Name"
                  required
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="form-group">
                <Field
                  name="contact.email"
                  component={FormInput}
                  label="Contact Email"
                  required
                />
              </div>
            </Col>
          </FormRow>
        )}
        <hr className="hr-sm" />
        <h4>Part Details</h4>
        <FormSection name={REPAIR_QUOTE_LINE_ITEM}>
          {!isNoCape && [
            <FormRow>
              <Col md={3}>
                <CurrencyField
                  name={AVG_OVERHAUL}
                  label={properCase(AVG_OVERHAUL)}
                  disabled={isAwaitingReply}
                />
              </Col>
              <Col md={1}>
                <Field
                  label="OH TAT"
                  name={OVERHAUL_TAT}
                  type="number"
                  className="text-right"
                  component={NumberInput}
                  roundDigits={0}
                  disabled={isAwaitingReply}
                />
              </Col>
              <Col md={3}>
                <CurrencyField
                  name={AVG_REPAIR}
                  label={properCase(AVG_REPAIR)}
                  disabled={isAwaitingReply}
                />
              </Col>
              <Col md={1}>
                <Field
                  label="SV TAT"
                  name={REPAIR_TAT}
                  type="number"
                  className="text-right"
                  component={NumberInput}
                  roundDigits={0}
                  disabled={isAwaitingReply}
                />
              </Col>
              <Col md={3}>
                <CurrencyField
                  name={AVG_TEST}
                  label={properCase(AVG_TEST)}
                  disabled={isAwaitingReply}
                />
              </Col>
              <Col md={1}>
                <Field
                  label="BT TAT"
                  name={TEST_TAT}
                  type="number"
                  className="text-right"
                  component={NumberInput}
                  roundDigits={0}
                  disabled={isAwaitingReply}
                />
              </Col>
              <Col md={3}>
                <Field
                  name="warranty_period"
                  component={Select}
                  options={toOptions(REPAIR_QUOTE_WARRANTY_PERIODS)}
                  label="Warranty Period"
                  disabled={isAwaitingReply}
                />
              </Col>
            </FormRow>,
            <FormRow>
              <Col md={4}>
                <CurrencyField
                  name={EVALUATION_FEE}
                  label={properCase(EVALUATION_FEE)}
                  disabled={isAwaitingReply}
                />
              </Col>
              <Col md={4}>
                <Field
                  component={FormInput}
                  name={BER_RATE}
                  label={"BER %"}
                  disabled={isAwaitingReply}
                  parse={value => value && parseInt(value, 10)}
                  addon={<i className="fa fa-percent" />}
                />
              </Col>
            </FormRow>
          ]}
          <FormRow>
            <Col md={12}>
              <Field name={NOTES} component={TextArea} label="Note" rows={5} />
            </Col>
          </FormRow>
        </FormSection>
      </AppForm.Body>
      <AppForm.AdditionalButtons>
        <button
          type="button"
          className="btn btn-danger"
          style={{ paddingLeft: "5px", paddingRight: "5px" }}
          onClick={e => {
            e.preventDefault();
            api.repairQuotes.delete(repairQuote.id).then(() => {
              toastr.success(
                "Success",
                `${repairQuote.code} successfully removed.`
              );
              props.close();
            });
          }}
        >
          Delete
        </button>
      </AppForm.AdditionalButtons>
    </AppForm>
  );
}

export default form({
  form: FORM_NAME,
  validate
})(withFieldAccessors(Form));



// WEBPACK FOOTER //
// ./app/modules/parts/components/Detail/RepairQuotes/Update/Form.jsx